import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import SignupForm from './Login/SignupForm.js';
import { getCookie } from '../util.js';

import {
  Pane,
  Checkbox,
  Button,
  Heading,
  toaster,
  Card,
  Text,
  Link,
  Paragraph,
  Strong,
  Spinner
} from 'evergreen-ui';

function AcceptInvite(props) {
  const [emailmode, setEmailmode] = useState(false);
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [pwconf, setPwconf] = useState('');
  const [checked, setChecked] = useState(false);
  const [errors, setErrors] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processingSignup, setProcessingSignup] = useState(false);

  const urlSearchParams = new URLSearchParams(window.location.search);
  const { invite, group, pubid } = Object.fromEntries(urlSearchParams.entries());

  let navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem('token') !== null) {
      navigate('/dashboard');
    } else {
      setLoading(false);
    }
  }, [navigate]);

  const handleSignupFromInvite = (e, user) => {
    e.preventDefault();
    setProcessingSignup(true);

    if (!invite) {
      console.error("No invite key found in URL parameters");
      toaster.danger('Invalid invitation link');
      setProcessingSignup(false);
      return;
    }

    const csrftoken = getCookie('csrftoken');

    fetch('/accept_invitation/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrftoken
      },
      body: JSON.stringify({key: invite})
    })
      .then(response => {
        return response.text().then(text => {
          if (response.status === 409) {
            toaster.danger('Invitation has already been accepted.');
            setProcessingSignup(false);
            return;
          }
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return text ? JSON.parse(text) : {};
        });
      })
      .then(data => {
        if (!data) return; // Handle case where invitation was already accepted

        const thistoken = getCookie('csrftoken');
        fetch('/api/v1/auth/register/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': thistoken
          },
          body: JSON.stringify(user)
        })
          .then(res => res.json())
          .then(data => {
            if (data.key) {
              const newtoken = getCookie('csrftoken');
              localStorage.clear();
              localStorage.setItem('token', data.key);
              localStorage.setItem('publisher', pubid);
              localStorage.setItem('publisher_name', group);
              fetch('/publisherusers/', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-CSRFToken': newtoken
                },
                body: JSON.stringify({publisher: pubid, token: data.key, role: 2})
              })
                .then(res => res.json())
                .then(data => {
                  localStorage.setItem('userid', data.user);
                  window.location.replace(`/dashboard`);
                })
                .catch(error => {
                  console.log(error);
                  setProcessingSignup(false);
                });
            } else {
              setEmail('');
              setPw('');
              setPwconf('');
              localStorage.clear();
              setErrors(true);
              setProcessingSignup(false);
            }
          })
          .catch(error => {
            console.log(error);
            setProcessingSignup(false);
          });
      })
      .catch(error => {
        console.log(error);
        setProcessingSignup(false);
      });
  }

  if (loading) {
    return (
      <Pane display="flex" alignItems="center" justifyContent="center" height="100vh" background="#46563d">
        <Spinner size={40} />
      </Pane>
    );
  }

  return (
    <Pane
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      background="#46563d"
      padding={16}
    >
      {/* Logo Section */}
      <Pane
        display="flex"
        flexDirection="column"
        alignItems="center"
        marginBottom={24}
      >
        <img
          src="/logo-workinglit.png"
          alt="WorkingLit Logo"
          width={200}
          style={{ marginBottom: 16 }}
        />
      </Pane>

      {/* Main Content Card */}
      <Card
        elevation={2}
        backgroundColor="white"
        padding={40}
        width="100%"
        maxWidth={520}
        borderRadius={8}
      >
        <Pane id="acceptInvite" width="100%">
          <Heading
            size={700}
            textAlign="center"
            marginBottom={8}
            color="#234361"
          >
            Join {group}
          </Heading>

          <Paragraph
            size={400}
            textAlign="center"
            marginBottom={24}
            color="#66788A"
          >
            You've been invited to collaborate on WorkingLit
          </Paragraph>

          <Pane
            display="flex"
            flexDirection="column"
            marginBottom={24}
            background="#F9F9FB"
            padding={16}
            borderRadius={4}
          >
            <Paragraph size={300} color="#66788A">
              <Strong>What is WorkingLit?</Strong> A publishing management platform that helps teams collaborate on book projects, manage royalties, and streamline workflows.
            </Paragraph>
          </Pane>

          <Pane display="flex" flexDirection="column">
            <Checkbox
              label={
                <Text size={300}>
                  I agree to the <Link href="https://docs.google.com/document/d/1_kjfaoJzEogR4GNToaibl4377smPQVXHaO1OAw4UJbI/edit?usp=sharing" target="_blank">terms and conditions</Link> and privacy policy.
                </Text>
              }
              checked={checked}
              onChange={e => setChecked(e.target.checked)}
              marginBottom={16}
            />

            {!emailmode && (
              <Button
                height={40}
                marginBottom={16}
                disabled={!checked || processingSignup}
                appearance="primary"
                intent="success"
                iconBefore={processingSignup ? <Spinner size={16} /> : null}
                onClick={e => setEmailmode(true)}
              >
                Create Your Account
              </Button>
            )}

            <SignupForm
              emailmode={emailmode}
              mode="invite"
              pubid={pubid}
              handleSignupFromInvite={handleSignupFromInvite}
            />
          </Pane>
        </Pane>
      </Card>

      <Pane marginTop={24} textAlign="center">
        <Text size={300} color="white">
          Already have an account? <Link href="/login" color="white" textDecoration="underline">Sign in</Link>
        </Text>
      </Pane>
    </Pane>
  );
}

export default AcceptInvite;
